import { useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

const HEADER_HIDDEN = "0";
const HEADER_SHOWN = "1";

export const useHeaderCollapse = () => {
	const [searchParams, setSearchParams] = useSearchParams();

	const headerVisibilityParam = searchParams.get("h");
	const headerCollapsed = headerVisibilityParam === HEADER_HIDDEN;

	useEffect(() => {
		if (headerVisibilityParam !== null) return;
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				next.set("h", HEADER_SHOWN);
				return next;
			},
			{ replace: true },
		);
	}, [headerVisibilityParam, setSearchParams]);

	/**
	 * Mirrored into a ref so {@link getHeaderCollapsed} and {@link setHeaderCollapsed} stay
	 * referentially stable — the wheel gestures read them through a context value that would
	 * otherwise churn on every header toggle. Synced during render so no handler sees a stale value.
	 */
	const headerVisibilityParamRef = useRef(headerVisibilityParam);
	headerVisibilityParamRef.current = headerVisibilityParam;

	const setHeaderCollapsed = useCallback(
		(collapsed: boolean) => {
			const target = collapsed ? HEADER_HIDDEN : HEADER_SHOWN;
			if (headerVisibilityParamRef.current === target) return;
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					next.set("h", target);
					return next;
				},
				{ replace: true },
			);
		},
		[setSearchParams],
	);

	const getHeaderCollapsed = useCallback(
		() => headerVisibilityParamRef.current === HEADER_HIDDEN,
		[],
	);

	return {
		headerCollapsed,
		setHeaderCollapsed,
		getHeaderCollapsed,
	};
};
